//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { readOnlyGuestAccountUuid, type AccountUuid, type Ref } from '@hcengineering/core'

import document from './plugin'
import { type Document, type PublicSite, type Teamspace } from './types'

/**
 * Maximum length of a site slug.
 * @public
 */
export const publicSiteSlugMaxLength = 64

/**
 * Slugs that can not be used for a public site because they are already taken by
 * platform routes or by endpoints served by the front service.
 *
 * @public
 */
export const publicSiteReservedSlugs = new Set([
  'account',
  'api',
  'assets',
  'calendar',
  'config.json',
  'files',
  'github',
  'guest',
  'login',
  'onboard',
  'print',
  'sign',
  'site',
  'static',
  'user',
  'workbench'
])

const cyrillicTranslit: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  і: 'i',
  ї: 'i',
  є: 'e',
  ґ: 'g'
}

/**
 * Normalizes an arbitrary user input into a valid slug.
 *
 * Lower cased latin letters, digits and hyphens only. Cyrillic is transliterated,
 * diacritics are stripped, any other character (including spaces) becomes a hyphen.
 *
 * @public
 */
export function normalizeSiteSlug (value: string): string {
  const transliterated = Array.from(value.toLowerCase())
    .map((char) => cyrillicTranslit[char] ?? char)
    .join('')

  return transliterated
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, publicSiteSlugMaxLength)
    .replace(/-$/g, '')
}

/**
 * Normalization to apply while the user is still typing. Same rules as
 * {@link normalizeSiteSlug} but a separator just typed is kept as a single trailing
 * hyphen so that multi word addresses can be typed at all.
 *
 * @public
 */
export function normalizeSiteSlugInput (value: string): string {
  const normalized = normalizeSiteSlug(value)
  const typingSeparator = value.length > 0 && /[^a-zA-Z0-9]$/.test(value)

  return typingSeparator && normalized.length > 0 && normalized.length < publicSiteSlugMaxLength
    ? `${normalized}-`
    : normalized
}

/**
 * Suggests a slug for a teamspace based on its name.
 * @public
 */
export function suggestSiteSlug (teamspaceName: string): string {
  return normalizeSiteSlug(teamspaceName)
}

/** @public */
export type SiteSlugError = 'empty' | 'tooLong' | 'invalid' | 'reserved' | 'duplicate'

/**
 * Validates a slug that has been typed by the user. Uniqueness is not checked here,
 * see {@link findSiteSlugConflict}.
 *
 * @public
 */
export function validateSiteSlug (slug: string): SiteSlugError | undefined {
  if (slug.length === 0) {
    return 'empty'
  }
  if (slug.length > publicSiteSlugMaxLength) {
    return 'tooLong'
  }
  if (normalizeSiteSlug(slug) !== slug) {
    return 'invalid'
  }
  if (publicSiteReservedSlugs.has(slug)) {
    return 'reserved'
  }

  return undefined
}

/**
 * Returns the site that already occupies the slug, either as its current slug or as
 * one of the slugs it is redirecting from. The site being edited is excluded.
 *
 * @public
 */
export function findSiteSlugConflict (
  sites: PublicSite[],
  slug: string,
  exclude?: Ref<PublicSite>
): PublicSite | undefined {
  return sites.find((it) => it._id !== exclude && (it.slug === slug || (it.previousSlugs ?? []).includes(slug)))
}

/**
 * Resolution of an incoming public url slug.
 *
 * `redirect` is set when the slug is an outdated one and the visitor has to be sent
 * to the current address of the site.
 *
 * @public
 */
export interface PublicSiteResolution {
  site: PublicSite
  redirect: boolean
}

/** Parsed public-site location under the configured workspace URL. @public */
export interface PublicSiteRoute {
  siteSlug: string
  pagePath: string[]
}

/**
 * Parses `/<workspace>/<site>/...` without treating a bare `/<site>` as public.
 * @public
 */
export function parsePublicSiteRoute (path: string[], workspaceSlug: string): PublicSiteRoute | undefined {
  if (path[0] !== workspaceSlug || path[1] === undefined || path[1] === '') {
    return undefined
  }

  return {
    siteSlug: path[1],
    pagePath: path.slice(2).filter((segment) => segment.length > 0)
  }
}

/** Builds an absolute public URL scoped by workspace. @public */
export function buildPublicSiteUrl (
  baseUrl: string,
  workspaceSlug: string,
  siteSlug: string,
  pagePath: string[] = []
): string {
  const path = [workspaceSlug, siteSlug, ...pagePath].join('/')
  return `${baseUrl.replace(/\/+$/, '')}/${path}/`
}

/** @public */
export function resolvePublicSite (sites: PublicSite[], slug: string): PublicSiteResolution | undefined {
  const current = sites.find((it) => it.slug === slug)
  if (current !== undefined) {
    return { site: current, redirect: false }
  }

  const previous = sites.find((it) => (it.previousSlugs ?? []).includes(slug))
  if (previous !== undefined) {
    return { site: previous, redirect: true }
  }

  return undefined
}

/**
 * Computes the new list of previous slugs after the site slug has been changed, so that
 * links to the old address keep working.
 *
 * @public
 */
export function withPreviousSlug (site: PublicSite, newSlug: string): string[] {
  if (site.slug === newSlug) {
    return site.previousSlugs ?? []
  }

  const previous = (site.previousSlugs ?? []).filter((it) => it !== newSlug)
  if (!previous.includes(site.slug)) {
    previous.push(site.slug)
  }

  return previous
}

/**
 * Only an owner of the teamspace is allowed to publish it, change publication settings
 * and unpublish it.
 *
 * @public
 */
export function canManagePublicSite (
  account: { uuid: AccountUuid } | undefined,
  teamspace: Pick<Teamspace, 'owners'> | undefined
): boolean {
  if (account === undefined || teamspace === undefined) {
    return false
  }

  return (teamspace.owners ?? []).includes(account.uuid)
}

/**
 * Page SEO is available only to the owner of the document's currently published teamspace.
 * @public
 */
export function canManagePublicPageSeo (
  account: { uuid: AccountUuid } | undefined,
  teamspace: Pick<Teamspace, '_id' | 'owners'> | undefined,
  site: Pick<PublicSite, 'teamspace'> | undefined
): boolean {
  return (
    site !== undefined &&
    teamspace !== undefined &&
    site.teamspace === teamspace._id &&
    canManagePublicSite(account, teamspace)
  )
}

/**
 * Anonymous account the public pages are served under. Server side space security only
 * grants a read only guest access to spaces it is a member of, so publishing a teamspace
 * adds this account to its members and unpublishing removes it. This is what keeps
 * unpublished teamspaces unreachable from the outside.
 *
 * @public
 */
export const publicSiteReaderAccount = readOnlyGuestAccountUuid

/** @public */
export function withPublicSiteReader (members: AccountUuid[]): AccountUuid[] {
  return members.includes(publicSiteReaderAccount) ? members : [...members, publicSiteReaderAccount]
}

/** @public */
export function withoutPublicSiteReader (members: AccountUuid[]): AccountUuid[] {
  return members.filter((it) => it !== publicSiteReaderAccount)
}

/**
 * Keeps workspace guest-access provenance on one remaining publication so the
 * last public site can restore the workspace setting on unpublish.
 *
 * @public
 */
export function findWorkspaceGuestAccessSuccessor (sites: PublicSite[], removing: PublicSite): PublicSite | undefined {
  if (removing.workspaceGuestAccessGranted !== true) {
    return undefined
  }

  return sites.find((site) => site._id !== removing._id)
}

/** @public */
export type PublicDocument = Pick<Document, '_id' | 'title' | 'parent' | 'rank' | 'publicSlug'>

/** Returns the configured root document, falling back to the teamspace overview. @public */
export function resolvePublicHomeDocument<T extends PublicDocument> (
  site: Pick<PublicSite, 'homeDocument'>,
  documents: T[]
): T | undefined {
  return site.homeDocument !== undefined ? documents.find((it) => it._id === site.homeDocument) : undefined
}

/**
 * Url segment of a single document inside the public site.
 * @public
 */
export function getPublicDocumentSlug (doc: PublicDocument): string {
  const customSlug = doc.publicSlug !== undefined ? normalizeSiteSlug(doc.publicSlug) : ''
  if (customSlug.length > 0) {
    return customSlug
  }

  const slug = normalizeSiteSlug(doc.title)
  return slug.length > 0 ? slug : doc._id
}

function byRank (a: PublicDocument, b: PublicDocument): number {
  return a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0
}

/**
 * Path segments of a document relative to the site root, following the document hierarchy.
 * Returns undefined when the hierarchy is broken (a parent is missing).
 *
 * @public
 */
export function getPublicDocumentPath (doc: PublicDocument, documents: PublicDocument[]): string[] | undefined {
  const byId = new Map(documents.map((it) => [it._id, it]))
  const segments: string[] = []

  let current: PublicDocument | undefined = doc
  const visited = new Set<Ref<Document>>()

  while (current !== undefined) {
    if (visited.has(current._id)) {
      return undefined
    }
    visited.add(current._id)
    segments.unshift(getPublicDocumentSlug(current))

    if (current.parent === document.ids.NoParent || current.parent == null) {
      return segments
    }

    current = byId.get(current.parent)
  }

  return undefined
}

/**
 * Resolves nested public url segments (e.g. `['releases', 'v1']`) into a document.
 * @public
 */
export function resolvePublicDocument (segments: string[], documents: PublicDocument[]): PublicDocument | undefined {
  let parent: Ref<Document> = document.ids.NoParent
  let found: PublicDocument | undefined

  for (const segment of segments) {
    const candidates = documents
      .filter((it) => (it.parent ?? document.ids.NoParent) === parent && getPublicDocumentSlug(it) === segment)
      .sort(byRank)

    found = candidates[0]
    if (found === undefined) {
      return undefined
    }
    parent = found._id
  }

  return found
}

/** @public */
export interface PublicSiteSeo {
  title: string
  description?: string
  canonical: string
  robots: string
}

/**
 * SEO metadata of a public site page.
 * @public
 */
export function buildPublicSiteSeo (params: {
  baseUrl: string
  workspaceSlug: string
  site: PublicSite
  teamspaceName: string
  pageTitle?: string
  pagePath?: string[]
}): PublicSiteSeo {
  const { baseUrl, workspaceSlug, site, teamspaceName, pageTitle, pagePath } = params

  const siteTitle = site.seoTitle !== undefined && site.seoTitle.length > 0 ? site.seoTitle : teamspaceName
  const title = pageTitle !== undefined && pageTitle.length > 0 ? `${pageTitle} — ${siteTitle}` : siteTitle

  const canonical = buildPublicSiteUrl(baseUrl, workspaceSlug, site.slug, pagePath)

  return {
    title,
    description: site.seoDescription !== undefined && site.seoDescription.length > 0 ? site.seoDescription : undefined,
    canonical,
    robots: site.noIndex === true ? 'noindex, nofollow' : 'index, follow'
  }
}
